import React, { useState, useEffect } from 'react';
import { MarketRealm } from './common/MarketRealmSwitcher';
import { SIDEBAR_ITEMS_BILINGUAL } from '../shared/constants/bilingualTitles';

export { SIDEBAR_ITEMS_BILINGUAL };

export interface ViewItem {
  id: string;
  label: string;
  sub: string;
  icon: string;
  hotkeyHint: string;
  route: string;
}

export const CANONICAL_VIEWS: ViewItem[] = [
  {
    id: 'view-regime-matrix',
    label: '1. 态势感知中枢',
    sub: 'Regime & Backtest',
    icon: '🧭',
    hotkeyHint: '1',
    route: '#/regime',
  },
  {
    id: 'view-trend-weather',
    label: '2. 趋势气象中枢',
    sub: 'Trend Weather',
    icon: '🌤️',
    hotkeyHint: '2, w',
    route: '#/weather',
  },
  {
    id: 'view-indicators-matrix',
    label: '3. 多周期指标流中枢',
    sub: 'Tech Indicators',
    icon: '📊',
    hotkeyHint: '3',
    route: '#/indicators',
  },
  {
    id: 'view-market-feeds',
    label: '4. 资产流与量化雷达',
    sub: 'Tickers & Radar',
    icon: '📈',
    hotkeyHint: '4',
    route: '#/feeds',
  },
  {
    id: 'view-dr-audit',
    label: '5. 容灾审计与异常日志',
    sub: 'DR Audit & Logs',
    icon: '🛡️',
    hotkeyHint: '5',
    route: '#/audit',
  },
  {
    id: 'view-infra-telemetry',
    label: '6. 节点拓扑与SLA监测',
    sub: 'Infra & SLA',
    icon: '⚙️',
    hotkeyHint: '6',
    route: '#/telemetry',
  },
  {
    id: 'view-grounding-hub',
    label: '7. 项目接地对齐',
    sub: 'Project Grounding Hub',
    icon: '🧭',
    hotkeyHint: '7, g',
    route: '#/grounding',
  },
  {
    id: 'view-integrations-audit',
    label: '8. 开放接入与服务治理',
    sub: 'Gateway & Audit',
    icon: '🔌',
    hotkeyHint: '8',
    route: '#/gateway',
  },
  {
    id: 'view-settings',
    label: '0. 系统与网关设置',
    sub: 'Settings & GW',
    icon: '⚙️',
    hotkeyHint: '0',
    route: '#/settings',
  },
];

export const REALM_VIEWS: Record<MarketRealm, ViewItem[]> = {
  crypto: SIDEBAR_ITEMS_BILINGUAL.crypto as ViewItem[],
  ashare: SIDEBAR_ITEMS_BILINGUAL.ashare as ViewItem[],
  macro: SIDEBAR_ITEMS_BILINGUAL.macro as ViewItem[],
  backtest: SIDEBAR_ITEMS_BILINGUAL.backtest as ViewItem[],
  system: SIDEBAR_ITEMS_BILINGUAL.system as ViewItem[],
};

export interface SidebarRailProps {
  activeView: string;
  onSelectView: (viewId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen?: boolean;
  currentRealm?: MarketRealm;
  onSelectRealm?: (realm: MarketRealm) => void;
}

export const SidebarRail: React.FC<SidebarRailProps> = ({
  activeView,
  onSelectView,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen = false,
  currentRealm = 'crypto',
  onSelectRealm: _onSelectRealm,
}) => {
  const [bjTime, setBjTime] = useState<string>('--:--:--');
  const [utcTime, setUtcTime] = useState<string>('--:--:-- Z');

  useEffect(() => {
    const updateClocks = () => {
      const now = new Date();
      // UTC+8 format
      const bjOptions: Intl.DateTimeFormatOptions = {
        timeZone: 'Asia/Shanghai',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      };
      setBjTime(now.toLocaleTimeString('zh-CN', bjOptions));

      // UTC format
      const utcHours = String(now.getUTCHours()).padStart(2, '0');
      const utcMinutes = String(now.getUTCMinutes()).padStart(2, '0');
      const utcSeconds = String(now.getUTCSeconds()).padStart(2, '0');
      setUtcTime(`${utcHours}:${utcMinutes}:${utcSeconds} Z`);
    };

    updateClocks();
    const timer = setInterval(updateClocks, 1000);
    return () => clearInterval(timer);
  }, []);

  const displayedViews = currentRealm && REALM_VIEWS[currentRealm] ? REALM_VIEWS[currentRealm] : CANONICAL_VIEWS;

  const hotkeySummary = React.useMemo(() => {
    const numHints = displayedViews
      .map((v) => {
        const match = v.hotkeyHint.match(/\b\d+\b/);
        return match ? parseInt(match[0], 10) : null;
      })
      .filter((n): n is number => n !== null);

    if (numHints.length === 0) return '1-N';
    const positiveNums = numHints.filter((n) => n > 0);
    const min = positiveNums.length > 0 ? Math.min(...positiveNums) : 1;
    const max = positiveNums.length > 0 ? Math.max(...positiveNums) : 1;
    const hasZero = numHints.includes(0);
    if (min === max) {
      return hasZero ? `${min}, 0` : `${min}`;
    }
    return hasZero ? `${min}-${max}, 0` : `${min}-${max}`;
  }, [displayedViews]);

  return (
    <aside
      className={`sidebar-rail ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
      id="sidebar-rail"
      role="navigation"
      aria-label="侧边功能导航栏"
    >
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="logo-box">⚡</div>
          <div className="brand-info">
            <span className="brand-title-text">MARKET HUB</span>
            <span className="brand-badge-text">QUANT DC</span>
          </div>
        </div>
        <button
          className="sidebar-collapse-btn"
          id="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          title="收起/展开侧边栏 (快捷键: [ )"
          aria-label="收起或展开侧边栏"
        >
          <span className="collapse-icon">◀</span>
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="sidebar-nav" role="tablist" aria-label="功能视图切换">
        <div className="nav-section-title">
          {currentRealm ? `${currentRealm.toUpperCase()} 视图导航` : '功能视图导航 (VIEWS)'}
        </div>
        {displayedViews.map((view) => {
          const isActive = activeView === view.id;
          return (
            <button
              key={view.id}
              className={`nav-item ${isActive ? 'active' : ''}`}
              id={`nav-${view.id.replace('view-', '')}`}
              role="tab"
              aria-selected={isActive}
              data-view={view.id}
              onClick={() => onSelectView(view.id)}
              title={`快捷键: ${view.hotkeyHint}`}
            >
              <span className="nav-icon">{view.icon}</span>
              <span className="nav-text">
                <span className="nav-label">{view.label}</span>
                <span className="nav-sub">{view.sub}</span>
              </span>
            </button>
          );
        })}
      </nav>

      {/* Sidebar Footer */}
      <div className="sidebar-footer">
        <div
          className="sidebar-status-card"
          title={`UTC+8 北京时间 / UTC 基准时间 (快捷键: ${hotkeySummary} 视图 · [ 折叠 · C Copilot)`}
        >
          <div className="sidebar-clock-row">
            <span className="pulse-dot" aria-hidden="true"></span>
            <span className="sidebar-clock-label">UTC+8 :</span>
            <span className="sidebar-clock-val mono" id="bj-clock">{bjTime}</span>
          </div>
          <div className="sidebar-clock-row utc-secondary">
            <span className="pulse-dot-spacer" aria-hidden="true"></span>
            <span className="sidebar-clock-label">UTC &nbsp; :</span>
            <span className="sidebar-clock-val mono" id="utc-clock">{utcTime}</span>
          </div>
        </div>
      </div>
    </aside>
  );
};
